import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import styles from './EditUser.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../../Auth';
import { useAxios } from '../../../../AxiosHandler';
import TextInput from '../../../Shared/TextInput/TextInput';
import DropdownInput from '../../../Shared/DropdownInput/DropdownInput';
import Card from '../../../Shared/Card/Card';
import { toast } from 'react-toastify';

function EditUser(props: EditUserProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();

    // Samme som på brukersida: hvilken seksjon som var åpen på /admin følger med
    // i history state, så tilbake-knappen her ikke lander på et kollapset /admin.
    const listUrl = "/admin" + ((location.state as any)?.listSearch ?? "");

    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();

    const [user, setUser] = useState<any | undefined>();
    const [councils, setCouncils] = useState<Array<any>>([]);
    const [newcouncil, setNewCouncil] = useState<string>("");
    const [councilConnections, setCouncilConnections] = useState<Array<any>>([]);
    const [password, setPassword] = useState<string>("");
    const [userBreeds, setUserBreeds] = useState<Array<any>>([]);
    const [emailPrefs, setEmailPrefs] = useState<Array<any>>([]);
    const [mainBreedId, setMainBreedId] = useState<string>("");

    function getUser() {
        axios.single({
            method: 'get',
            url: "/api/users/byid/" + params.userid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setUser(res.data);
            setMainBreedId(res.data.MAIN_BREED_ID ?? "");
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function updateUser() {
        axios.single({
            method: 'post',
            url: "/api/users/updateUser/",
            data: {
                userid: user.ID,
                email: user.EMAIL,
                firstname: user.FIRSTNAME,
                lastname: user.LASTNAME,
                councilConnections: councilConnections,
                emailPrefs: emailPrefs,
                mainBreedId: mainBreedId || null
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            toast.success(t("user.edituser.update.popup"));
            navigate(`/user/${user.ID}`);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getUserBreeds() {
        axios.single({
            method: 'get',
            url: "/api/users/breedsForUser/" + params.userid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setUserBreeds(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getUserEmailPrefs() {
        axios.single({
            method: 'get',
            url: "/api/users/emailPrefs/" + params.userid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setEmailPrefs(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getEmailPref(breedId: number, field: string): boolean {
        const pref = emailPrefs.find(p => Number(p.BREED_ID) === breedId);
        if (!pref) return false;
        return pref[field] === 1 || pref[field] === true;
    }

    function setEmailPref(breedId: number, field: string, value: boolean) {
        const existing = emailPrefs.find(p => Number(p.BREED_ID) === breedId);
        if (existing) {
            setEmailPrefs(emailPrefs.map(p =>
                Number(p.BREED_ID) === breedId ? { ...p, [field]: value } : p
            ));
        } else {
            setEmailPrefs([...emailPrefs, {
                BREED_ID: breedId,
                EMAIL_BREEDINGDOG: false,
                EMAIL_BREEDINGREQUEST: false,
                EMAIL_SICKNESS: false,
                [field]: value
            }]);
        }
    }

    function getCouncils() {
        axios.single({
            method: 'get',
            url: "/api/users/getCouncils",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setCouncils(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getCouncilConnections() {
        axios.single({
            method: 'get',
            url: "/api/users/getCouncilConnections/" + params.userid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setCouncilConnections(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    useEffect(() => {
        if (params.userid != undefined && auth.selectedBreed != undefined) {
            getUser();
        }
    }, [params.userid, auth.selectedBreed]);

    useEffect(() => {
        getCouncils();
        getCouncilConnections();
        getUserBreeds();
        getUserEmailPrefs();
    }, []);

    const initials = [user?.FIRSTNAME, user?.LASTNAME]
        .filter(Boolean)
        .map((s: string) => s[0].toUpperCase())
        .join('') || '?';

    const title = user
        ? `${user.FIRSTNAME ?? ''} ${user.LASTNAME ?? ''}`.trim() || user.EMAIL
        : t("user.edituser.title");

    return (
        <div className="maincontent">
            <div className={styles.edituser}>
                <h1>{title}</h1>
                <hr/>
                <div className={styles.topbuttons}>
                    <button className="btn btn-primary" onClick={() => navigate(listUrl)}>
                        {t("user.edituser.buttons.back")}
                    </button>
                    <button className="btn btn-success" onClick={updateUser}>
                        {t("user.edituser.buttons.updateuser")}
                    </button>
                </div>
                <hr/>
                {user != undefined &&
                    <Container className={styles.container}>
                        <Row className={styles.row}>

                            {/* Basic info card */}
                            <Col sm="12" md="12" lg="6" xl="4" className={styles.col}>
                                <div className={styles.cardwrapper}>
                                    <div className={styles.avatar}>
                                        <span className={styles.avatarInitials}>{initials}</span>
                                    </div>
                                    <Card title={t("user.cards.generaloverview.title")}>
                                        <TextInput
                                            title={t("user.edituser.input.email")}
                                            placeholder={t("user.edituser.input.email_placeholder")}
                                            onChange={(event: any) => setUser({...user, EMAIL: event.target.value})}
                                            value={user?.EMAIL}
                                        />
                                        <TextInput
                                            title={t("user.edituser.input.firstname")}
                                            placeholder={t("user.edituser.input.firstname_placeholder")}
                                            onChange={(event: any) => setUser({...user, FIRSTNAME: event.target.value})}
                                            value={user?.FIRSTNAME}
                                        />
                                        <TextInput
                                            title={t("user.edituser.input.lastname")}
                                            placeholder={t("user.edituser.input.lastname_placeholder")}
                                            onChange={(event: any) => setUser({...user, LASTNAME: event.target.value})}
                                            value={user?.LASTNAME}
                                        />
                                        <TextInput
                                            title={t("Passord")}
                                            placeholder={t("Passord")}
                                            onChange={(event: any) => setPassword(event.target.value)}
                                            value={password}
                                        />
                                        <DropdownInput
                                            title={t("user.edituser.mainbreed.title")}
                                            onChange={(event: any) => setMainBreedId(event.target.value)}
                                            value={mainBreedId}
                                        >
                                            <option value="">{t("user.edituser.mainbreed.none")}</option>
                                            {userBreeds.map(breed => (
                                                <option key={breed.BREED_ID} value={breed.BREED_ID}>{breed.BREED_NAME}</option>
                                            ))}
                                        </DropdownInput>
                                    </Card>
                                </div>
                            </Col>

                            {/* Councils card */}
                            <Col sm="12" md="12" lg="6" xl="4" className={styles.col}>
                                <Card title={t("user.edituser.councillist.title")}>
                                    <div className={styles.councilList}>
                                        {councilConnections?.map((council: any) => {
                                            const councilData = councils?.find(c => c.ID == council.COUNCIL_ID);
                                            if (!councilData) return null;
                                            const canEdit = auth.user.userdata?.COUNCIL_ACCESS?.find(
                                                (c: any) => c.COUNCIL_ID == council.COUNCIL_ID && c.ACCESSLEVEL == "admin"
                                            ) != undefined || auth.isSysadmin();
                                            const isLast = councilConnections.filter(i => councils.map(c => c.ID.toString()).includes(i.COUNCIL_ID.toString())).length == 1;
                                            const setAccess = (level: string) => {
                                                councilConnections.find(i => i.COUNCIL_ID == council.COUNCIL_ID).ACCESSLEVEL = level;
                                                setCouncilConnections([...councilConnections]);
                                            };
                                            return (
                                                <div key={council.COUNCIL_ID} className={styles.councilItem}>
                                                    <span className={styles.councilItemName}>{councilData.NAME}</span>
                                                    {canEdit ? (
                                                        <div className={styles.councilItemActions}>
                                                            <div className={`btn-group btn-group-sm ${styles.accessToggle}`}>
                                                                <button
                                                                    className={`btn btn-outline-primary${council.ACCESSLEVEL === 'admin' ? ' active' : ''}`}
                                                                    onClick={() => setAccess('admin')}
                                                                >{t("user.edituser.councillist.admin")}</button>
                                                                <button
                                                                    className={`btn btn-outline-primary${council.ACCESSLEVEL === 'user' ? ' active' : ''}`}
                                                                    onClick={() => setAccess('user')}
                                                                >{t("user.edituser.councillist.user")}</button>
                                                                <button
                                                                    className={`btn btn-outline-primary${council.ACCESSLEVEL === 'dogowner' ? ' active' : ''}`}
                                                                    onClick={() => setAccess('dogowner')}
                                                                >{t("user.edituser.councillist.dogowner")}</button>
                                                            </div>
                                                            <button
                                                                className={`btn btn-sm btn-outline-danger ${styles.removeBtn}`}
                                                                disabled={isLast}
                                                                onClick={() => setCouncilConnections(councilConnections.filter(i => i.COUNCIL_ID !== council.COUNCIL_ID))}
                                                                title={t("user.edituser.buttons.removecouncil")}
                                                            >✕</button>
                                                        </div>
                                                    ) : (
                                                        <span className={styles.accessBadge}>{council.ACCESSLEVEL}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className={styles.addCouncilRow}>
                                        <select
                                            className={`form-select form-select-sm ${styles.addCouncilSelect}`}
                                            value={newcouncil}
                                            onChange={(e) => setNewCouncil(e.target.value)}
                                        >
                                            <option value=""></option>
                                            {councils
                                                .filter(filt => !councilConnections.map(i => Number(i.COUNCIL_ID)).includes(filt.ID))
                                                .filter(c => auth.user?.userdata?.COUNCIL_ACCESS?.find((a: any) => a.COUNCIL_ID == c.ID && a.ACCESSLEVEL == "admin") != undefined || auth.isSysadmin())
                                                .map((council: any) =>
                                                    <option key={council.ID} value={council.ID}>{council.NAME}</option>
                                                )
                                            }
                                        </select>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            disabled={!newcouncil}
                                            onClick={() => {
                                                setCouncilConnections([...councilConnections, {
                                                    USER_ID: params.userid,
                                                    COUNCIL_ID: newcouncil,
                                                    ACCESSLEVEL: "user"
                                                }]);
                                                setNewCouncil("");
                                            }}
                                        >+</button>
                                    </div>
                                </Card>
                            </Col>

                            {/* Email preferences card */}
                            {userBreeds.length > 0 &&
                                <Col sm="12" md="12" lg="12" xl="8" className={styles.col}>
                                    <Card title={t("user.edituser.email_preferences.title")}>
                                        <table className={styles.emailPrefsGrid}>
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>{t("user.edituser.email_preferences.breedingdog")}</th>
                                                    <th>{t("user.edituser.email_preferences.breedingrequest")}</th>
                                                    <th>{t("user.edituser.email_preferences.sickness")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userBreeds.map(breed => (
                                                    <tr key={breed.BREED_ID}>
                                                        <td>{breed.BREED_NAME}</td>
                                                        <td>
                                                            <input type="checkbox"
                                                                checked={getEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGDOG')}
                                                                onChange={(e) => setEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGDOG', e.target.checked)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input type="checkbox"
                                                                checked={getEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGREQUEST')}
                                                                onChange={(e) => setEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGREQUEST', e.target.checked)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input type="checkbox"
                                                                checked={getEmailPref(breed.BREED_ID, 'EMAIL_SICKNESS')}
                                                                onChange={(e) => setEmailPref(breed.BREED_ID, 'EMAIL_SICKNESS', e.target.checked)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </Card>
                                </Col>
                            }

                        </Row>
                    </Container>
                }
            </div>
        </div>
    );
};

class EditUserProps {}

export default EditUser;
